import React from "react";
import axios from "axios";
import { API_URL } from "src/scenes/App";
import { Box, Grid } from "@mui/material";
import { useState, useEffect } from "react";
import {
    LottieLoadingIndicator,
    ModalCountryPhoneInput,
    StandardModal,
    StandardModalActionButton,
    StandardModalErrorText,
    StandardModalFormGrid,
    StandardModalFormGroup,
    StandardModalInput,
    StandardModalLabel,
    StandardModalDescription,
    StandardModalHeading,
    StandardModalSectionTitle,
    portalColors
} from "src/components";
import styled, { css } from "styled-components";
import Toast from "src/components/Toast";
import pluralize from "pluralize";
import { capitalizeString, customNames } from "src/utils/helpers";
import ConvertIcon from "src/img/convert.svg";

const Required = styled.span`
    color: ${portalColors.danger};
    font-style: italic;
    font-weight: 400;
    ::before {
        content: "(required)";
        font-size: 0.8rem;
    }
`;

const RadioButton = styled.button`
    position: relative;
    width: min(100%, 15.625rem);
    height: 2.5rem;
    border: 1px solid ${p => p.active ? portalColors.text : portalColors.inputBorder};
    border-radius: var(--calendar-radius-control, 8px);
    text-align: center;
    font-family: Manrope, Helvetica, sans-serif;
    font-weight: 700;
    color: ${p => p.active ? "var(--calendar-bg, #FFFFFF)" : portalColors.text};
    background-color: ${p => p.active ? portalColors.text : portalColors.surfaceSoft};
    box-shadow: ${portalColors.inputShadow};
    cursor: pointer;

    ${(props) =>
        props.active &&
        css`
            background-color: ${portalColors.text};
            color: var(--calendar-bg, #FFFFFF) !important;
            font-weight: 700;
        `}
    ${(props) =>
        props.disabled &&
        css`
            pointer-events: none;
            color: ${portalColors.textSoft};
        `}
`;

const StyledInput = styled(StandardModalInput)`
    width: 100%;
    max-width: none;
    height: 42px;
    text-align: left;
    padding: 0 12px;
`;

const ClientFormContent = styled(Box)`
    width: 100%;
    max-width: none;
    display: grid;
    gap: 1rem;
`;

const Circle = styled.div`
    position: absolute;
    right: 0;
    bottom: 9px;
    margin-right: 10px;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background-color: #76C00D;
    display: flex;
    justify-content: center;
    align-items: center;
    visibility: hidden;

    ${(props) =>
        props.active &&
        css`
            visibility: visible;
        `}
    ${(props) =>
        props.disabled &&
        css`
            pointer-events: none;
        `}
`;

const CheckMark = styled.span`
    color: white;
    font-weight: 700;
    font-size: 10px;
`;

const TrialUserType = styled.div`
    width: 100%;
    height: 42px;
    border: 1px solid ${portalColors.inputBorder};
    border-radius: var(--calendar-radius-control, 8px);
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.875rem;
    font-weight: 700;
    color: ${portalColors.text};
    background: ${portalColors.surfaceSoft};
    box-shadow: ${portalColors.inputShadow};
    margin: 0.75rem 0 0;
`;

const ConvertSection = styled.div`
    margin: 0.25rem 0 1.25rem;
`;

const ConvertSectionTitle = styled(StandardModalSectionTitle)`
    margin: 0 0 0.5rem;
`;

const ConvertSectionDescription = styled(StandardModalDescription)`
    max-width: 650px;
    margin: 0 0 1.25rem;
`;

const ConvertButtons = styled.div`
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;
`;

const ConvertChoiceButton = styled.button`
    width: 15.625rem;
    height: 2.5rem;
    border: 1px solid ${portalColors.inputBorder};
    border-radius: var(--calendar-radius-control, 8px);
    text-align: center;
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.875rem;
    font-weight: 700;
    background-color: ${portalColors.surfaceSoft};
    color: ${portalColors.text};
    box-shadow: ${portalColors.inputShadow};
    cursor: pointer;

    &:hover {
        background-color: ${portalColors.surfaceTertiary};
    }
`;

const ConvertDialogContent = styled.div`
    width: 650px;
    max-width: calc(100vw - 48px);
    min-height: 330px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 42px 70px 36px;
    text-align: center;
`;

const ConvertDialogIcon = styled.img`
    width: 72px;
    height: 72px;
    object-fit: contain;
    filter: brightness(0);

    [data-theme="dark"] & {
        filter: brightness(0) invert(1);
    }
`;

const ConvertDialogTitle = styled(StandardModalHeading)`
    margin: 10px 0 20px;
    font-size: 2rem;
    text-align: center;
`;

const ConvertDialogMessage = styled(StandardModalDescription)`
    width: 520px;
    max-width: 100%;
    margin: 0 0 18px;
    text-align: center;
`;

const ConvertDialogNote = styled(ConvertDialogMessage)`
    margin-bottom: 42px;
    font-weight: 500;
`;

const ConvertDialogActions = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 20px;
`;

const ConvertDialogError = styled.div`
    width: 520px;
    max-width: 100%;
    margin: -24px 0 24px;
    color: ${portalColors.danger};
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.875rem;
    font-weight: 500;
`;

const ClientDetailsForm = (props) => {
    const { 
        showDialog, 
        handleClose, 
        client, 
        enableSuccessModal, 
        isClientFormEdit, 
        shortTermDurationUnit, 
        countryCode, 
        token, 
        isZeroTrial, 
        isFreeTrialLicense 
    } = props;
    const [formData, setFormData] = useState(props.client);
    const [errorList, setErrorList] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [conversionTargetIsClient, setConversionTargetIsClient] = useState(true);
    const [showConvertDialog, setShowConvertDialog] = useState(false);
    const [isConvertingUser, setIsConvertingUser] = useState(false);

    const handleInputChange = (event, key) => {
        const updatedFormData = { ...formData, [key]: event.target.value };
        setFormData(updatedFormData);
    };

    const handlePhoneNumber = (phone) => {
        if (phone !== "" && !phone.startsWith("+")) {
            phone = "+" + phone;
        }

        const updatedFormData = { ...formData, phoneNumber: phone };
        setFormData(updatedFormData);
    };

    const handleFormSubmit = () => {
        setErrorList([]);
        const requiredFields = ["firstName", "lastName", "email"];
        let status = true;
        requiredFields.forEach((field) => {
            if (!formData[field].trim()) {
                status = false;
            }
        });

        if (!status) {
            return setErrorList((prevErrorList) => [...prevErrorList, "Please fill all required fields."]);
        }
        if (!validateEmail(formData.email)) {
            return setErrorList((prevErrorList) => [...prevErrorList, "Invalid email format."]);
        }
        setIsLoading(true);
        return isClientFormEdit ? updateClient() : createClient();
    };

    const createClient = () => {
        axios.post(`${API_URL}/company/clientLicense`, formData, {
            headers: {
                Authorization: "Bearer " + token
            }
        })
            .then((response) => {
                let newLicensedEmployee = {
                    employeeUuid: response.data.licensedEmployee.employeeUuid,
                    firstName: response.data.licensedEmployee.firstName,
                    lastName: response.data.licensedEmployee.lastName,
                    email: response.data.licensedEmployee.email,
                    phoneNumber: response.data.licensedEmployee.phoneNumber,
                };
                setIsLoading(false);
                enableSuccessModal(newLicensedEmployee);
            })
            .catch((error) => {
                setIsLoading(false);
                return setErrorList(error.getApiErrorMessages());
            });
    };

    const updateClient = () => {
        axios.put(`${API_URL}/company/clientLicense/${formData.uuid}`, formData, {
            headers: {
                Authorization: "Bearer " + token
            }
        })
            .then(() => {
                setIsLoading(false);
                enableSuccessModal(formData);
            })
            .catch(error => {
                setIsLoading(false);
                return setErrorList(error.getApiErrorMessages());
            });
    };

    const handleDeleteClient = () => {
        const isConfirmed = window.confirm("Are you sure you want to remove the client?");
        if (!isConfirmed) {
            return false;
        }
        setIsLoading(true);
        axios.delete(`${API_URL}/company/clientLicense/${formData.uuid}`, {
            headers: {
                Authorization: "Bearer " + token
            }
        })
            .then(() => {
                setIsLoading(false);
                props.refreshTableOnClientDelete();
            })
            .catch(error => {
                setIsLoading(false);
                Toast.error(error.getApiErrorDetails().message);
            });
    };

    const openConvertDialog = (nextIsClient) => {
        setErrorList([]);
        setConversionTargetIsClient(nextIsClient);
        setShowConvertDialog(true);
    };

    const closeConvertDialog = () => {
        if (isConvertingUser) return;
        setErrorList([]);
        setShowConvertDialog(false);
    };

    const convertTrialClient = () => {
        setIsConvertingUser(true);
        axios.put(`${API_URL}/clients/${formData.uuid}/userType`, {
            isClient: conversionTargetIsClient,
        }, {
            headers: {
                Authorization: "Bearer " + token
            }
        })
            .then(() => {
                Toast.success("User converted successfully");
                setShowConvertDialog(false);
                handleClose();
                props.onTrialClientConverted?.();
            })
            .catch(error => {
                setErrorList(error.getApiErrorMessages());
            })
            .finally(() => {
                setIsConvertingUser(false);
            });
    };

    const validateEmail = (email) => {
        return /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(email);
    };

    // prevents console warns when one of the input values is null
    const filterInputValue = (value) => [null].includes(value) ? "" : value;

    const renderErrors = errorList.map((error, index) => {
        return errorList.length ? <StandardModalErrorText key={index}>{error}</StandardModalErrorText>
            : "";
    });

    useEffect(() => {
        setFormData(client);
    }, [props]);

    const targetUserTypeName = conversionTargetIsClient
        ? capitalizeString(pluralize.singular(customNames.clients))
        : capitalizeString(pluralize.singular(customNames.employees));

    return (
        <>
            <StandardModal
                open={showDialog && !showConvertDialog}
                onClose={handleClose}
                title={!isClientFormEdit ? `Add New ${!isZeroTrial ? "Trial " : ""} ${capitalizeString(pluralize.singular(customNames.clients))}` : "Edit User"}
                description={!isClientFormEdit ? `Enter the relevant ${capitalizeString(pluralize.singular(customNames.clients))} details accordingly.${!isFreeTrialLicense ? ` Adding a new ${capitalizeString(pluralize.singular(customNames.clients))} will deduct 1 license.` : ""}` : undefined}
                widthMode="compactWide"
                bodyGap="1rem"
                footer={(
                    <div style={{ display: "flex", justifyContent: "space-between", width: "100%", gap: "0.75rem", flexWrap: "wrap" }}>
                        <div>
                            {isClientFormEdit && !isLoading && (
                                <StandardModalActionButton
                                    $variant="danger"
                                    width="9.375rem" height="2.3125rem"
                                    onClick={() => handleDeleteClient()}
                                >
                                    Delete User
                                </StandardModalActionButton>
                            )}
                        </div>
                        <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.75rem", flexWrap: "wrap", marginLeft: "auto" }}>
                            {isLoading ? <LottieLoadingIndicator containerHeight="2rem" margin="0" /> : (
                                <>
                                    <StandardModalActionButton
                                        width="8.75rem"
                                        onClick={handleClose}
                                        className="form-btns disabled"
                                    >
                                        Cancel
                                    </StandardModalActionButton>
                                    <StandardModalActionButton
                                        $variant="primary"
                                        width="8.75rem"
                                        type="submit"
                                        onClick={() => handleFormSubmit()}
                                        className="form-btns disabled"
                                    >
                                        {isClientFormEdit ? "Save" : "Add User"}
                                    </StandardModalActionButton>
                                </>
                            )}
                        </div>
                    </div>
                )}
            >
                <ClientFormContent>
                    <Grid container spacing={2}>
                        <Grid item sm={12} md={4}>
                            <StandardModalLabel>{isClientFormEdit ? "User Type" : "Select User Type"}</StandardModalLabel>
                            {isClientFormEdit ? (
                                <TrialUserType>Trial {capitalizeString(pluralize.singular(customNames.clients))}</TrialUserType>
                            ) : (
                                <>
                                    <input type="radio" name="userType" value="client" style={{ position: "fixed", visibility: "hidden" }} />
                                    <RadioButton active={true}
                                        style={{ margin: "0" }}
                                    >
                                        {capitalizeString(pluralize.singular(customNames.clients))}
                                        <Circle active={true}
                                        ><CheckMark>&#x2713;</CheckMark></Circle>
                                    </RadioButton>
                                </>
                            )}
                        </Grid>
                    </Grid>

                    {isClientFormEdit && (
                        <ConvertSection>
                            <ConvertSectionTitle>Convert Trial {capitalizeString(pluralize.singular(customNames.clients))} User (Optional)</ConvertSectionTitle>
                            <ConvertSectionDescription>
                            Transfer this trial {pluralize.singular(customNames.clients).toLowerCase()} to your main user list as an employee or client. This will add them to your subscription, and their existing trial data related to business features will be lost.
                            </ConvertSectionDescription>
                            <ConvertButtons>
                                <ConvertChoiceButton
                                    type="button"
                                    onClick={() => openConvertDialog(false)}
                                >
                                Convert to {capitalizeString(pluralize.singular(customNames.employees))} List
                                </ConvertChoiceButton>
                                <ConvertChoiceButton
                                    type="button"
                                    onClick={() => openConvertDialog(true)}
                                >
                                Convert to {capitalizeString(pluralize.singular(customNames.clients))} List
                                </ConvertChoiceButton>
                            </ConvertButtons>
                        </ConvertSection>
                    )}

                    <StandardModalFormGrid columns="repeat(3, minmax(0, 1fr))">
                        <StandardModalFormGroup>
                            <StandardModalLabel>First Name <Required /></StandardModalLabel>
                            <StyledInput placeholder="First Name"
                                value={filterInputValue(formData.firstName)}
                                onChange={e => handleInputChange(e, "firstName")}
                                style={{ textTransform: "capitalize" }}
                            />
                        </StandardModalFormGroup>
                        <StandardModalFormGroup>
                            <StandardModalLabel>Last Name <Required /></StandardModalLabel>
                            <StyledInput required placeholder="Last Name"
                                value={filterInputValue(formData.lastName)}
                                onChange={e => handleInputChange(e, "lastName")}
                                style={{ textTransform: "capitalize" }}
                            />
                        </StandardModalFormGroup>
                        <StandardModalFormGroup>
                            <StandardModalLabel>Email <Required /></StandardModalLabel>
                            <StyledInput required placeholder="Email"
                                value={filterInputValue(formData.email)}
                                onChange={e => handleInputChange(e, "email")}
                            />
                        </StandardModalFormGroup>
                    </StandardModalFormGrid>

                    <StandardModalFormGrid columns="repeat(3, minmax(0, 1fr))">
                        <StandardModalFormGroup>
                            <StandardModalLabel>Mobile</StandardModalLabel>

                            <ModalCountryPhoneInput
                                autoFormat={false}
                                prefix={"+"}
                                country={countryCode.toLowerCase()}
                                value={filterInputValue(formData.phoneNumber)}
                                onChange={phone => handlePhoneNumber(phone)}
                                placeholder="Mobile Number"
                                errors={[]}
                            />
                        </StandardModalFormGroup>
                        <StandardModalFormGroup>
                            <StandardModalLabel>{capitalizeString(pluralize.singular(customNames.clients))} Type (Optional)</StandardModalLabel>
                            <StyledInput placeholder={`${capitalizeString(pluralize.singular(customNames.clients))} Type`}
                                value={filterInputValue(formData.clientType)}
                                onChange={e => handleInputChange(e, "clientType")}
                            />
                        </StandardModalFormGroup>
                        <StandardModalFormGroup>
                            <StandardModalLabel>License Duration</StandardModalLabel>
                            <StyledInput placeholder="License Duration"
                                value={isClientFormEdit ? formData.licenseDuration : `${shortTermDurationUnit}`}
                                readOnly={true}
                            />
                        </StandardModalFormGroup>
                    </StandardModalFormGrid>

                    {renderErrors}
                </ClientFormContent>
            </StandardModal>
            <StandardModal
                open={showConvertDialog}
                onClose={closeConvertDialog}
                widthMode="large"
                hideCloseButton
                bodyPadding="0"
            >
                <ConvertDialogContent>
                    <ConvertDialogIcon src={ConvertIcon} alt="" />
                    <ConvertDialogTitle>
                        Convert Trial {capitalizeString(pluralize.singular(customNames.clients))} User to {targetUserTypeName}
                    </ConvertDialogTitle>
                    <ConvertDialogMessage>
                        Are you sure you want to convert this Trial {capitalizeString(pluralize.singular(customNames.clients))} user to a {targetUserTypeName}? This action will remove the user from the Sales &amp; Promotions trial client list and move them to your main {targetUserTypeName.toLowerCase()} user list.
                    </ConvertDialogMessage>
                    <ConvertDialogNote>
                        Please note: all permissions, data, and history associated with their client status will be permanently lost.
                    </ConvertDialogNote>
                    {!!errorList.length && (
                        <ConvertDialogError>
                            {errorList.join(" ")}
                        </ConvertDialogError>
                    )}
                    <ConvertDialogActions>
                        <StandardModalActionButton
                            width="8.75rem"
                            onClick={closeConvertDialog}
                        >
                            Back
                        </StandardModalActionButton>
                        <StandardModalActionButton
                            $variant="primary"
                            width="8.75rem"
                            onClick={convertTrialClient}
                            disabled={isConvertingUser}
                        >
                            {isConvertingUser ? "Confirming..." : "Confirm"}
                        </StandardModalActionButton>
                    </ConvertDialogActions>
                </ConvertDialogContent>
            </StandardModal>
        </>
    );
};

export default ClientDetailsForm;
